import { useState } from 'react'
import { ArrowLeft, Download, Share2, Eye, EyeOff, FileText, BarChart3, Presentation } from 'lucide-react'
import { SprintDetails, SprintReport } from '../pages/SprintReport'
import { exportService } from '../lib/exportService'

interface SprintReportResultsProps {
  report: SprintReport
  sprintDetails: SprintDetails
  onNewAnalysis: () => void
}

export default function SprintReportResults({ report, sprintDetails, onNewAnalysis }: SprintReportResultsProps) {
  const [showAnalysisLog, setShowAnalysisLog] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  const handleExport = async (type: 'pdf' | 'excel' | 'powerpoint') => {
    try {
      setExporting(type)
      
      switch (type) {
        case 'pdf':
          await exportService.exportToPDF(report, sprintDetails)
          break
        case 'excel':
          await exportService.exportToExcel(report, sprintDetails)
          break
        case 'powerpoint':
          await exportService.exportToPowerPoint(report, sprintDetails)
          break
      }
    } catch (error) {
      console.error(`Export failed:`, error)
      alert(`Failed to export ${type.toUpperCase()}. Please try again.`)
    } finally {
      setExporting(null)
    }
  }

  const handleShare = () => {
    const shareableLink = exportService.generateShareableLink(report, sprintDetails)
    navigator.clipboard.writeText(shareableLink).then(() => {
      alert('Shareable link copied to clipboard!')
    }).catch(() => {
      alert('Failed to copy link. Please try again.')
    })
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sprint Report</h1>
          <p className="text-gray-600">Analysis completed for "{sprintDetails.label}" sprint</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowAnalysisLog(!showAnalysisLog)}
            className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {showAnalysisLog ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{showAnalysisLog ? 'Hide' : 'Show'} Analysis Log</span>
          </button>
          
          <button
            onClick={() => window.location.reload()}
            className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <span>New Analysis</span>
          </button>
          
          {/* Export Dropdown */}
          <div className="relative group">
            <button
              disabled={exporting !== null}
              className="flex items-center space-x-2 px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span>{exporting ? `Exporting ${exporting}...` : 'Export'}</span>
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleExport('pdf')}
                disabled={exporting !== null}
                className="flex items-center space-x-2 w-full px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                <span>PDF Report</span>
              </button>
              <button
                onClick={() => handleExport('excel')}
                disabled={exporting !== null}
                className="flex items-center space-x-2 w-full px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Excel Data</span>
              </button>
              <button
                onClick={() => handleExport('powerpoint')}
                disabled={exporting !== null}
                className="flex items-center space-x-2 w-full px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <Presentation className="w-4 h-4" />
                <span>PowerPoint</span>
              </button>
            </div>
          </div>
          
          <button
            onClick={handleShare}
            className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Share2 className="w-4 h-4" />
            <span>Share</span>
          </button>
        </div>
      </div>

      {/* Analysis Log Section */}
      {showAnalysisLog && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">📋 Analysis Log</h3>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium text-gray-700">📊 Data Fetching</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                <div className="bg-white p-3 rounded border">
                  <div className="text-2xl font-bold text-blue-600">{report.analysisLog.dataFetching.cardsFound}</div>
                  <div className="text-xs text-gray-600">Cards Found</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-2xl font-bold text-green-600">{report.analysisLog.dataFetching.listsFound}</div>
                  <div className="text-xs text-gray-600">Lists Found</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-2xl font-bold text-purple-600">{report.analysisLog.dataFetching.commentsFetched}</div>
                  <div className="text-xs text-gray-600">Comments</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-2xl font-bold text-orange-600">{report.analysisLog.dataFetching.activityRecords}</div>
                  <div className="text-xs text-gray-600">Activity Records</div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-700">⏱️ Processing Times</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                <div className="bg-white p-3 rounded border">
                  <div className="text-lg font-bold text-blue-600">{formatTime(report.analysisLog.processingTime.dataFetching)}</div>
                  <div className="text-xs text-gray-600">Data Fetching</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-lg font-bold text-green-600">{formatTime(report.analysisLog.processingTime.metricsCalculation)}</div>
                  <div className="text-xs text-gray-600">Metrics</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-lg font-bold text-purple-600">{formatTime(report.analysisLog.processingTime.insightsGeneration)}</div>
                  <div className="text-xs text-gray-600">Insights</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="text-lg font-bold text-orange-600">{formatTime(report.analysisLog.processingTime.total)}</div>
                  <div className="text-xs text-gray-600">Total Time</div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-700">📊 List Distribution</h4>
              <div className="bg-white p-3 rounded border mt-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(report.analysisLog.metricsCalculation.listDistribution).map(([listName, count]) => (
                    <div key={listName} className="flex justify-between">
                      <span className="text-gray-600">{listName}:</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {report.analysisLog.dataFetching.errors.length > 0 && (
              <div>
                <h4 className="font-medium text-red-700">❌ Errors</h4>
                <div className="bg-red-50 border border-red-200 rounded p-3 mt-2">
                  {report.analysisLog.dataFetching.errors.map((error, index) => (
                    <div key={index} className="text-red-700 text-sm">{error}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sprint Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Sprint Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{report.summary.totalCards}</div>
            <div className="text-sm text-gray-600">Total Cards</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{report.summary.completedCards}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{report.summary.incompleteCards}</div>
            <div className="text-sm text-gray-600">Incomplete</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{report.summary.velocity}</div>
            <div className="text-sm text-gray-600">Velocity (cards/day)</div>
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Key Insights</h2>
        <div className="space-y-4">
          {report.insights.bottlenecks.length > 0 && (
            <div>
              <h3 className="font-medium text-red-700 mb-2">🚨 Bottlenecks</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {report.insights.bottlenecks.map((bottleneck, index) => (
                  <li key={index}>{bottleneck}</li>
                ))}
              </ul>
            </div>
          )}
          
          {report.insights.commonIssues.length > 0 && (
            <div>
              <h3 className="font-medium text-orange-700 mb-2">⚠️ Common Issues</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {report.insights.commonIssues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
          
          {report.insights.improvementSuggestions.length > 0 && (
            <div>
              <h3 className="font-medium text-green-700 mb-2">💡 Improvement Suggestions</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {report.insights.improvementSuggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Retrospective */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Retrospective</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-green-700 mb-2">✅ What Went Well</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {report.retrospective.whatWentWell.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-red-700 mb-2">❌ What Went Wrong</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {report.retrospective.whatWentWrong.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="mt-6">
          <h3 className="font-medium text-blue-700 mb-2">🎯 Action Items</h3>
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            {report.retrospective.actionItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onNewAnalysis}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>New Analysis</span>
        </button>
      </div>
    </div>
  )
} 